import { Loading } from '@shape-ds/components';
import { AppContext } from 'appContext';
import {
  useCommentMutation,
  useInvalidateRecordsQueries,
  useOptimisticUpdateQuery,
  useRecordsQuery,
} from 'queries/events/records';
import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalNotification } from 'hooks';
import { RecordCategory } from 'queries/events/records/types';
import './style.scss';
import { RecordsFiles } from '../RecordsFiles';
import RecordsMessageItem from './RecordsMessageItem';
import { RecordsInput } from '../RecordsInput';

interface RecordsMessagesProps {
  eventId: string;
  disableFiles?: boolean;
}

export const RecordsMessages = ({ eventId, disableFiles = false }: RecordsMessagesProps) => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const { displaySnackbar } = useGlobalNotification();

  const optimisticUpdate = useOptimisticUpdateQuery(['RECORDS', eventId]);
  const refetchQueries = useInvalidateRecordsQueries(eventId);

  const { data, isFetching } = useRecordsQuery(
    { eventId },
    {
      refetchOnMount: true,
    },
  );
  const newCommentMutation = useCommentMutation();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (payload: string) => {
    if (!payload.trim()) return Promise.resolve(undefined as any);

    const revertUpdate = optimisticUpdate({
      user_email: state.auth.user?.email || '',
      category: RecordCategory.COMMENT,
      comment: payload,
    });

    return newCommentMutation.mutateAsync(
      {
        eventId,
        text: payload,
        category: RecordCategory.COMMENT,
      },
      {
        onSuccess: () => {
          displaySnackbar({
            message: t('messages:success.message'),
            type: 'success',
          });
        },
        onError: () => {
          displaySnackbar({
            message: t('messages:fetch_error.message'),
            type: 'error',
          });
          revertUpdate();
        },
        onSettled: () => {
          refetchQueries();
        },
      },
    );
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [data?.messages]);

  return (
    <>
      <div className="records-container" ref={messagesEndRef}>
        {data?.messages.map((message) => (
          <RecordsMessageItem message={message} scope={data?.scope} key={message.id} />
        ))}
        {isFetching && (
          <div data-testid="loading" style={{ textAlign: 'center' }}>
            <Loading />
          </div>
        )}
        <div ref={messagesEndRef}></div>
      </div>

      <RecordsInput handleSubmit={handleSubmit} />

      {!disableFiles && <RecordsFiles eventId={eventId} />}
    </>
  );
};
